import React from 'react';
import { css } from 'emotion';
import { STEPS_TO_SWITCH, QUOTES, FAQS, FEATURES } from './constants';
import { SM_SCREEN_MAX } from '../../modules/Styleguide';
import {
  IconContainer,
  Video,
  Container,
  SearchSection,
  SearchSectionBackground,
  SearchContent,
  HowToSwitchSection,
  HowToSwitchContent,
  StepsContainer,
  StepContainer,
  StepTitle,
  QuotesSection,
  QuotesContent,
  QuotesContainer,
  QuoteContainer,
  QuoteImage,
  QuoteTitle,
  Quote,
  QuoteIconContainer,
  QuoteIcon,
  FaqSection,
  FaqContent,
  FeaturesSection,
  Feature,
  FeatureHeader,
  FeatureContent,
  FeatureAsset,
  FeatureInfo,
  FeatureWell,
  WellHead,
  WellTail,
  FeatureMain,
  FeaturesContainer,
  FeatureSectionHeader,
  HowToSwitchSubheader,
  FAQHeader,
  SectionHeader,
  QuoteText,
  SearchHeading,
  SearchSubHeading,
  FeatureHeading,
  FeatureSubHeading,
} from './styles';
import TypeaheadOption from './components/TypeaheadOption';
import {
  ImportForm,
  TypeaheadOption as TypeaheadOptionProps,
} from './components/ImportForm';
import { Footer } from '../../components/Footer';
import { BigQuoteIcon } from '../../shared/Icon/components/BigQuoteIcon';
import { FAQ } from './components/FAQ';
import { LinkText } from '../../shared/Link';

type ImportScreenProps = {
  onSubmit: (param: string) => void;
  onBlur: () => void;
  isSearching: boolean;
  onSelectTypeahead: (options: TypeaheadOptionProps[]) => void;
  onTypeaheadSearch: (value: string) => void;
  rssTypeaheadResults: TypeaheadOptionProps[];
  rssFeedError: {
    message: string;
  };
  fetchingRSSFeedImport: boolean;
  rssFeedIsValid: boolean;
  rssFeedMetadata: {
    authorName: string;
    authorEmail: string;
    image: string;
    isExplicit: boolean;
    itunesCategory: string;
    language: string;
    podcastName: string;
    podcastDescription: string;
    episodeCount: number;
  };
  rssFeedUrl: string;
};

function ImportScreen({
  isSearching,
  rssTypeaheadResults,
  onTypeaheadSearch,
  onSelectTypeahead,
  onSubmit,
  fetchingRSSFeedImport,
  rssFeedUrl,
  rssFeedIsValid,
  rssFeedError,
  rssFeedMetadata,
}: ImportScreenProps) {
  const hasDataToContinue: boolean =
    !fetchingRSSFeedImport && rssFeedMetadata && !!rssFeedMetadata.podcastName;

  return (
    <Container>
      <SearchSection>
        <SearchSectionBackground>
          <SearchContent>
            <div
              className={css`
                padding: 0 0 75px;

                @media (max-width: ${SM_SCREEN_MAX}px) {
                  padding: 0 0 30px;
                }
              `}
            >
              <SearchHeading>Switch your podcast to Anchor</SearchHeading>
              <div style={{ marginTop: 10 }}>
                <SearchSubHeading>
                  Host as many episodes as you want, publish your show
                  everywhere, and earn
                  <br /> money when people listen—without spending a cent.
                </SearchSubHeading>
              </div>
            </div>
            <ImportForm
              isSearching={isSearching}
              onSelectTypeahead={onSelectTypeahead}
              onTypeaheadSearch={onTypeaheadSearch}
              rssTypeaheadResults={rssTypeaheadResults}
              renderTypeaheadOption={option => {
                const { image, podcastName, authorName } = option;
                return (
                  <TypeaheadOption
                    image={image}
                    podcastName={podcastName}
                    authorName={authorName}
                  />
                );
              }}
              rssFeedError={rssFeedError}
              hasDataToContinue={hasDataToContinue}
              fetchingRSSFeedImport={fetchingRSSFeedImport}
              rssFeedIsValid={rssFeedIsValid}
              onClickGetStarted={() => {
                onSubmit(rssFeedUrl);
              }}
              rssFeedMetadata={rssFeedMetadata}
            />
          </SearchContent>
        </SearchSectionBackground>
      </SearchSection>
      <FeaturesSection>
        <FeatureSectionHeader>
          <SectionHeader>
            Switching to Anchor saves time and money, giving you more resources
            to put into your podcast.
          </SectionHeader>
        </FeatureSectionHeader>
        <FeaturesContainer>
          {FEATURES.map(feature => {
            const {
              header,
              subheader,
              assetUrl,
              smAssetUrl,
              info,
              altText,
              width,
            } = feature;

            return (
              <Feature key={header}>
                <FeatureWell>
                  <WellHead />
                  <WellTail className="well-tail" />
                </FeatureWell>
                <FeatureMain>
                  <FeatureHeader>
                    <div style={{ marginBottom: 10 }}>
                      <FeatureHeading>{header}</FeatureHeading>
                    </div>
                    <FeatureSubHeading>{subheader}</FeatureSubHeading>
                  </FeatureHeader>
                  <FeatureContent>
                    <FeatureAsset>
                      <picture>
                        <source
                          srcSet={smAssetUrl}
                          media={`(max-width: ${SM_SCREEN_MAX}px)`}
                        />
                        <img
                          // @ts-ignore;
                          loading="lazy"
                          width={width}
                          alt={altText}
                          src={assetUrl}
                        />
                      </picture>
                    </FeatureAsset>
                    <FeatureInfo>{info}</FeatureInfo>
                  </FeatureContent>
                </FeatureMain>
              </Feature>
            );
          })}
        </FeaturesContainer>
      </FeaturesSection>
      <HowToSwitchSection>
        <HowToSwitchContent>
          <SectionHeader style={{ color: '#ffffff' }}>
            How switching works
          </SectionHeader>
          <HowToSwitchSubheader>
            <h2>
              From your listeners’ perspective, it’ll be like nothing happened
            </h2>
          </HowToSwitchSubheader>
          <StepsContainer>
            {STEPS_TO_SWITCH.map((step, index) => (
              <StepContainer key={step.title}>
                <IconContainer>
                  <picture>
                    <source
                      srcSet={step.imageUrl}
                      media={`(max-width: ${SM_SCREEN_MAX}px)`}
                    />
                    <img
                      // @ts-ignore;
                      loading="lazy"
                      width={100}
                      alt={`Step ${index + 1} icon`}
                      src={step.retinaImageUrl}
                    />
                  </picture>
                </IconContainer>
                <StepTitle>
                  <h2>{`${index + 1}. ${step.title}`}</h2>
                </StepTitle>
                <p>{step.description}</p>
              </StepContainer>
            ))}
          </StepsContainer>
          <Video>
            <iframe
              title="How to switch to Anchor"
              width="920"
              height="500"
              src="https://www.youtube.com/embed/sg9wTQlt4vs"
              frameBorder="0"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen={true}
            />
          </Video>
        </HowToSwitchContent>
      </HowToSwitchSection>
      <QuotesSection>
        <QuotesContent>
          <div
            className={css`
              padding: 8px 0 100px;

              @media (max-width: ${SM_SCREEN_MAX}px) {
                padding: 8px 0 35px;
              }
            `}
          >
            <SectionHeader>
              Anchor powers more podcasts than any other platform
            </SectionHeader>
          </div>
          <QuotesContainer>
            {QUOTES.map(quote => (
              <QuoteContainer
                isReverse={quote.isReverse}
                key={quote.podcastName}
              >
                <QuoteImage isReverse={quote.isReverse}>
                  <picture>
                    <source
                      srcSet={quote.podcastCoverartImageUrl}
                      media={`(max-width: ${SM_SCREEN_MAX}px)`}
                    />
                    <img
                      // @ts-ignore;
                      loading="lazy"
                      width={quote.imgWidth}
                      alt={`Cover art for podcast ${quote.podcastName}`}
                      src={quote.podcastCoverartImageRetinaUrl}
                    />
                  </picture>
                </QuoteImage>
                <Quote isReverse={quote.isReverse}>
                  <QuoteIconContainer isReverse={quote.isReverse}>
                    <QuoteIcon isReverse={quote.isReverse}>
                      <BigQuoteIcon />
                    </QuoteIcon>
                  </QuoteIconContainer>
                  <QuoteText isReverse={quote.isReverse}>
                    <p>{quote.quote}</p>
                    <QuoteTitle>
                      <p>{`${quote.authorName} from`}</p>
                      <LinkText to={quote.podcastUrl}>
                        {quote.podcastName}
                      </LinkText>
                    </QuoteTitle>
                  </QuoteText>
                </Quote>
              </QuoteContainer>
            ))}
          </QuotesContainer>
        </QuotesContent>
      </QuotesSection>
      <FaqSection>
        <div style={{ paddingTop: 8, paddingBottom: 8, marginBottom: 37 }}>
          <SectionHeader>
            <p>More questions?</p>
            <p>Check out these handy FAQs.</p>
          </SectionHeader>
        </div>
        <FaqContent>
          <div>
            <FAQHeader>About Anchor</FAQHeader>
            <hr />
            {FAQS.filter(({ isSwitchQuestion }) => !isSwitchQuestion).map(
              faq => (
                <FAQ
                  key={faq.question}
                  question={faq.question}
                  answer={faq.renderAnswer()}
                />
              )
            )}
          </div>
          <div
            className={css`
              margin-top: 100px;

              @media (max-width: ${SM_SCREEN_MAX}px) {
                margin-top: 50px;
              }
            `}
          >
            <FAQHeader>About switching</FAQHeader>
            <hr />
            {FAQS.filter(({ isSwitchQuestion }) => isSwitchQuestion).map(
              faq => (
                <FAQ
                  key={faq.question}
                  question={faq.question}
                  answer={faq.renderAnswer()}
                />
              )
            )}
          </div>
        </FaqContent>
      </FaqSection>
      <Footer />
    </Container>
  );
}

export { ImportScreen };
